import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert, 
  Chip,
  Button,
  Link,
  Grid,
  Divider
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  Store as StoreIcon,
  ContentCopy as ContentCopyIcon,
  LinkOff as LinkOffIcon
} from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import api from '../../../utils/api';

// Helper to compute a verified product URL from scraped data (Ashley CSV)
// Rules:
// - Use scraped productUrl if provided by backend
// - Else, prefer model/handle/slug for product handle; fallback to title
// - If category exists -> collections/{category}/products/{handle}
//   Else -> products/{handle}
const computeVerifiedUrl = (data) => {
  if (!data) return '';
  
  // If backend provides a direct URL, use it
  if (data.productUrl) return data.productUrl;
  
  const base = 'https://www.tallahasseediscountfurniture.com';
  
  // Create handle from preferred fields: model contains the handle from CSV backend
  let handle = (data.model || data.handle || data.slug || '').toString().trim();
  console.log('[URL Builder] Using handle from model:', handle);
  
  if (!handle && data.title) {
    handle = data.title
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    console.log('[URL Builder] Generated handle from title:', handle);
  }
  
  // If no handle can be created, return empty URL
  if (!handle) {
    console.log('[URL Builder] No handle available, cannot generate URL');
    return '';
  }
  
  // Try to get category slug for collections path
  const categorySlug = (data.productCategory || data.category || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
  
  // Build URL with or without category
  if (categorySlug && categorySlug.length > 0) {
    return `${base}/collections/${categorySlug}/products/${handle}`;
  }
  
  return `${base}/products/${handle}`;
};

// Convert a handle/slug like "stonemarch-slate" into a title like "Stonemarch Slate"
const humanizeSlug = (value) => {
  if (!value) return '';
  return value
    .toString()
    .trim()
    .replace(/[\-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const ScrapedProductInfo = ({ sku, onDataUpdate, refreshToken }) => {
  const [loading, setLoading] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);
  const [error, setError] = useState(null);

  const scrapeProduct = async (skuToScrape) => {
    if (!skuToScrape?.trim()) return;

    setLoading(true);
    setError(null);
    
    try {
      console.log(`[Scraping] Starting scrape for SKU: ${skuToScrape}`);
      
      const response = await api.get(`/csv-data/search/${encodeURIComponent(skuToScrape.trim())}`);

      if (response.data.success) {
        const raw = response.data.data || {};
        console.log('[CSV Data] Raw CSV data received:', raw);
        
        // Map CSV data structure to expected format
        const mappedData = {
          title: raw.title,
          description: raw.description,
          price: raw.price ? `$${raw.price.toFixed(2)}` : null,
          originalPrice: raw.compareAtPrice ? `$${raw.compareAtPrice.toFixed(2)}` : null,
          brand: raw.vendor,
          model: raw.handle, // CSV handle becomes model for URL generation
          sku: raw.sku,
          category: raw.category,
          availability: raw.status === 'active' ? 'In Stock' : 'Out of Stock',
          cost: raw.cost,
          weight: raw.weight,
          barcode: raw.barcode,
          inventoryQty: raw.inventoryQty,
          images: raw.images || [],
          specifications: raw.specifications || [],
          tags: raw.tags || []
        };
        
        // Ensure we always have a readable title: prefer CSV title; else derive from handle
        const slugSource = (mappedData.model || mappedData.handle || '');
        const derivedTitle = (mappedData.title && mappedData.title !== 'Title not available')
          ? mappedData.title
          : (slugSource ? humanizeSlug(slugSource) : '');
        
        // Compute URL using the mapped data
        const productUrl = computeVerifiedUrl(mappedData);
        console.log('[CSV Data] Computed URL:', productUrl, 'from handle:', mappedData.model);
        
        const enriched = { 
          ...mappedData, 
          productUrl, 
          title: derivedTitle || mappedData.title || 'Title not available'
        };
        setScrapedData(enriched);
        console.log('[CSV Data] Enriched data:', enriched);
        
        // Notify parent component of new data (ensure productUrl is present)
        if (onDataUpdate) {
          onDataUpdate(enriched);
        }
      } else {
        setError(response.data.message || 'Failed to scrape product data');
        setScrapedData(null);
      }
    } catch (err) {
      console.error('[CSV Data] Error:', err);
      if (err.response?.status === 404) {
        setError(`No product found in Ashley furniture inventory for SKU "${skuToScrape}"`);
      } else {
        setError(err.response?.data?.message || 'Failed to search Ashley furniture data');
      }
      setScrapedData(null);
    } finally {
      setLoading(false);
    }
  };

  // Auto-scrape when SKU changes or an explicit refresh is triggered
  useEffect(() => {
    if (sku?.trim()) {
      const handler = setTimeout(() => {
        scrapeProduct(sku);
      }, 500); // Debounce for snappy refresh

      return () => clearTimeout(handler);
    } else {
      setScrapedData(null);
      setError(null);
    }
  }, [sku, refreshToken]);

  const handleRefresh = () => {
    if (sku?.trim()) {
      scrapeProduct(sku);
    }
  };

  if (!sku?.trim()) {
    return null;
  }

  return (
    <Paper sx={{ p: 2, mt: 2, bgcolor: 'rgba(76, 175, 80, 0.08)', border: '1px solid', borderColor: 'success.light' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StoreIcon color="success" />
          Ashley Furniture Data
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
          variant="outlined"
          color="success"
        >
          Refresh
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <CircularProgress size={20} color="success" />
          <Typography variant="body2" color="text.secondary">
            Searching Ashley furniture inventory...
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      )}

      {scrapedData && !loading && (
        <Box>
          {/* Product Title and Link */}
          {scrapedData.title && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {scrapedData.title}
              </Typography>
              {scrapedData.productUrl ? (
                <>
                  <Link
                    href={scrapedData.productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.875rem', mb: 0.5 }}
                  >
                    View on Website
                    <OpenInNewIcon fontSize="small" />
                  </Link>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', color: 'text.secondary' }}>
                      {scrapedData.productUrl}
                    </Typography>
                    <Tooltip title="Copy URL">
                      <IconButton
                        size="small"
                        onClick={() => navigator.clipboard?.writeText(scrapedData.productUrl)}
                        aria-label="Copy URL"
                      >
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                  <LinkOffIcon fontSize="small" />
                  <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                    URL not available in data sheet
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Price Information */}
          {(scrapedData.price || scrapedData.originalPrice) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                PRICING
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                {scrapedData.price && (
                  <Chip 
                    label={`Current: ${scrapedData.price}`} 
                    color="success" 
                    variant="outlined" 
                    size="small" 
                  />
                )}
                {scrapedData.originalPrice && (
                  <Chip 
                    label={`Original: ${scrapedData.originalPrice}`} 
                    color="default" 
                    variant="outlined" 
                    size="small" 
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Product Details */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              PRODUCT DETAILS
            </Typography>
            <Grid container spacing={1}>
              {scrapedData.brand && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    <strong>Brand:</strong> {scrapedData.brand}
                  </Typography>
                </Grid>
              )}
              {scrapedData.model && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    <strong>Model:</strong> {scrapedData.model}
                  </Typography>
                </Grid>
              )}
              {scrapedData.sku && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    <strong>Store SKU:</strong> {scrapedData.sku}
                  </Typography>
                </Grid>
              )}
              {scrapedData.availability && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    <strong>Availability:</strong> {scrapedData.availability}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>

          {/* Description */}
          {scrapedData.description && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                DESCRIPTION
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
                {scrapedData.description.length > 200 
                  ? `${scrapedData.description.substring(0, 200)}...`
                  : scrapedData.description
                }
              </Typography>
            </Box>
          )}

          {/* Specifications */}
          {scrapedData.specifications && scrapedData.specifications.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                SPECIFICATIONS
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {scrapedData.specifications.slice(0, 5).map((spec, index) => (
                  <Chip 
                    key={index}
                    label={spec} 
                    size="small" 
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: '20px' }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Images */}
          {scrapedData.images && scrapedData.images.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                PRODUCT IMAGES
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {scrapedData.images.slice(0, 3).map((imageUrl, index) => (
                  <Box
                    key={index}
                    component="img"
                    src={imageUrl}
                    alt={`Product ${index + 1}`}
                    sx={{
                      width: 60,
                      height: 60,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 1 }} />
          
          {/* Metadata */}
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Source: {scrapedData.source || 'Ashley Furniture CSV'} • 
            {scrapedData.scrapedAt && ` ${new Date(scrapedData.scrapedAt).toLocaleTimeString()}`}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ScrapedProductInfo;
